import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Rating,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SendIcon from '@mui/icons-material/Send';
import { useAppDispatch } from '../store/hooks';
import { addNotification } from '../store/slices/uiSlice';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const [rating, setRating] = useState<number | null>(5);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!feedbackText.trim() && !rating) return;

    setSubmitted(true);
    setTimeout(() => {
      dispatch(
        addNotification({
          type: 'success',
          message: 'Thank you for your feedback! We review all suggestions daily.',
        })
      );
      setSubmitted(false);
      setFeedbackText('');
      onClose();
    }, 800);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#091512',
          backgroundImage:
            'radial-gradient(ellipse at top, rgba(132, 204, 22, 0.1), transparent 70%)',
          border: '1px solid rgba(132, 204, 22, 0.3)',
          borderRadius: 3,
          color: '#ffffff',
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RateReviewIcon sx={{ color: '#84cc16' }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#ffffff' }}>
            User Feedback
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ca3af' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        {submitted ? (
          <Alert severity="success" sx={{ bgcolor: 'rgba(132, 204, 22, 0.2)', color: '#84cc16' }}>
            Feedback submitted! Thank you for helping us make GeoVera better.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ color: '#d1d5db', mb: 1 }}>
                How is your experience with GeoVera so far?
              </Typography>
              <Rating
                value={rating}
                onChange={(_, newValue) => setRating(newValue)}
                size="large"
                sx={{
                  color: '#84cc16',
                  '& .MuiRating-iconEmpty': { color: '#4b5563' },
                }}
              />
            </Box>

            <TextField
              label="Tell us what you like or what we should add..."
              multiline
              rows={3}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="e.g. Please add land record layer for Karnataka / Maharashtra..."
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#ffffff',
                  bgcolor: 'rgba(13, 29, 23, 0.6)',
                  '& fieldset': { borderColor: 'rgba(132, 204, 22, 0.3)' },
                  '&:hover fieldset': { borderColor: '#84cc16' },
                  '&.Mui-focused fieldset': { borderColor: '#84cc16' },
                },
                '& .MuiInputLabel-root': { color: '#9ca3af' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#84cc16' },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: '#9ca3af', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={submitted || (!feedbackText.trim() && !rating)}
          sx={{
            bgcolor: '#84cc16',
            color: '#000000',
            fontWeight: 800,
            textTransform: 'none',
            '&:hover': { bgcolor: '#65a30d' },
            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: '#6b7280' },
          }}
        >
          Submit Feedback
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;
